/**
 * PicksViewToggle - Tab strip for switching between Current Picks and the streaming preview.
 * Visible only when generating with existing picks, or when pending picks are queued.
 */

import type React from 'react';

interface PendingPicks {
  picks: unknown[];
}

interface PicksViewToggleProps {
  viewMode: 'current' | 'streaming';
  onSelectCurrent: () => void;
  onSelectStreaming: () => void;
  onApplyPending: () => void;
  currentPicksCount: number;
  streamingParlaysCount: number;
  pendingPicks: PendingPicks | null;
  isGenerating: boolean;
}

export function PicksViewToggle({
  viewMode,
  onSelectCurrent,
  onSelectStreaming,
  onApplyPending,
  currentPicksCount,
  streamingParlaysCount,
  pendingPicks,
  isGenerating,
}: PicksViewToggleProps): React.ReactElement {
  return (
    <div className="flex items-center gap-1 bg-theme-surface rounded-lg p-1 mb-3">
      <button
        type="button"
        onClick={onSelectCurrent}
        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition ${
          viewMode === 'current'
            ? 'bg-theme-primary-fill text-theme-on-primary-fill shadow-sm'
            : 'text-theme-text-secondary hover:bg-theme-surface-hover'
        }`}
      >
        Current Picks ({currentPicksCount})
      </button>
      {isGenerating ? (
        <button
          type="button"
          onClick={onSelectStreaming}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition ${
            viewMode === 'streaming'
              ? 'bg-theme-primary-fill text-theme-on-primary-fill shadow-sm'
              : 'text-theme-text-secondary hover:bg-theme-surface-hover'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-theme-primary animate-pulse" />
          Generating... ({streamingParlaysCount})
        </button>
      ) : pendingPicks ? (
        <button
          type="button"
          onClick={onApplyPending}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition bg-theme-success/15 text-theme-success hover:bg-theme-success/25"
        >
          New Picks ({pendingPicks.picks.length}) — Apply
        </button>
      ) : null}
    </div>
  );
}
